import { AbstractContentLoader } from './abstract-content-loader';
import { FormService } from '../services/form-service';
import { Logger } from '../utils/logger';
import { DomUtils } from '../utils/dom-utils';
export class DocumentLoader extends AbstractContentLoader {
    constructor(config) {
        super(config.ajaxUrl, config.nonce, config.postId, config.formId, config.contentId);
        this.config = config;
    }
    /**
     * Initialize the document loader
     */
    initialize() {
        Logger.debug('Initializing document loader', this.config);
        // Register hidden input callback
        this.registerHiddenInputCallback();
        // Set up content refresh functionality
        this.setupContentRefresh();
        // If AJAX form loading is enabled, load the form
        if (this.config.useAjaxFormLoading) {
            this.loadFormViaAjax();
        }
    }
    /**
     * Register hidden input callback for form submission
     */
    registerHiddenInputCallback() {
        FormService.registerHiddenInputCallback({
            type: 'document',
            formId: this.formId,
            postId: this.postId,
        });
    }
    /**
     * Get the content container element
     */
    getContentContainer() {
        return DomUtils.findMainContentContainer(this.config.contentId);
    }
    /**
     * Get the form container element
     */
    getFormContainer() {
        return DomUtils.getElementById(DomUtils.getFormContainerId(this.contentId));
    }
    /**
     * Get the loading container element
     */
    getLoadingContainer() {
        return DomUtils.getElementById(DomUtils.getLoadingContainerId('document', this.contentId));
    }
    /**
     * Set up content refresh functionality for after form submission
     */
    setupContentRefresh() {
        // Create global function for content refresh
        window.gpsaFetchDocumentContent = () => {
            this.refreshContent();
        };
        Logger.debug('Document content refresh function registered');
    }
    /**
     * Refresh content after form submission
     */
    async refreshContent() {
        try {
            // Hide form and show loading
            DomUtils.hideElement(this.getFormContainer());
            this.showLoading(this.config.settings.gpsa_content_loading_message);
            // Get updated content
            const response = await this.ajaxService.getDocumentContent(this.postId, this.formId);
            this.hideLoading();
            if (response.has_access) {
                // User has access, replace content in theme container
                const contentContainer = this.getContentContainer();
                if (contentContainer.length > 0) {
                    DomUtils.replaceContent(contentContainer, response.content);
                }
                else {
                    Logger.warn('Could not find content container for document replacement');
                }
            }
            else {
                // Still no access, show form again
                DomUtils.showElement(this.getFormContainer(), response.content);
            }
        }
        catch (error) {
            this.handleAjaxError(error, 'document content refresh');
        }
    }
    /**
     * Load form via AJAX (when AJAX form loading is enabled)
     */
    async loadFormViaAjax() {
        try {
            const loadingMessage = this.config.settings.gpsa_ajax_form_loading_message;
            this.showLoading(loadingMessage);
            const response = await this.ajaxService.getDocumentForm(this.postId, this.formId, this.postId);
            this.hideLoading();
            DomUtils.showElement(this.getFormContainer(), response.content);
            Logger.debug('Document form loaded via AJAX');
        }
        catch (error) {
            this.handleAjaxError(error, 'AJAX form loading');
        }
    }
    /**
     * Static method to create and initialize document loader from config
     * @param config
     */
    static createFromConfig(config) {
        const loader = new DocumentLoader(config);
        loader.initialize();
        return loader;
    }
}
